import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { z } from 'zod';
import { useCreateGallery } from '../../../../app/hooks/gallery/useCreateGallery';
import { useEvents } from '../../../../app/hooks/event/useEvents';
import { useStore } from '../../../../app/store';

interface IUseCreateGalleryModalController {
  leadId: string;
}

interface IGalleryFormValues {
  name: string;
  eventId: string;
  selectionLimit: string;
  clientMessage: string;
}

export function useCreateGalleryModalController({
  leadId,
}: IUseCreateGalleryModalController) {
  const { t } = useTranslation();
  const { events } = useEvents();
  const { createGalleryAsync, createGalleryIsPending } = useCreateGallery();

  const { handleCloseModal, handleOpenManageGalleryModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
      handleOpenManageGalleryModal: state.modal.handleOpenManageGalleryModal,
    })),
  );

  const schema = z.object({
    name: z
      .string()
      .trim()
      .nonempty(t('gallery.createModal.errors.nameRequired')),
    eventId: z.string(),
    selectionLimit: z
      .string()
      .refine(
        (value) => value === '' || Number.parseInt(value, 10) >= 1,
        t('gallery.createModal.errors.selectionLimitMin'),
      ),
    clientMessage: z.string(),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IGalleryFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      eventId: '',
      selectionLimit: '',
      clientMessage: '',
    },
  });

  const eventOptions = useMemo(
    () =>
      (events ?? [])
        .filter((event) => event.leadId === leadId)
        .map((event) => ({ value: event.id, text: event.description })),
    [events, leadId],
  );

  const onSubmit = handleSubmit(async (values) => {
    const gallery = await createGalleryAsync({
      leadId,
      name: values.name.trim(),
      eventId: values.eventId || undefined,
      selectionLimit: values.selectionLimit
        ? Number.parseInt(values.selectionLimit, 10)
        : undefined,
      clientMessage: values.clientMessage.trim() || undefined,
    });

    handleOpenManageGalleryModal(gallery.id);
  });

  return {
    register,
    control,
    errors,
    eventOptions,
    onSubmit,
    createGalleryIsPending,
    handleCloseModal,
  };
}
